import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { scheduleCampaign } from '../../services/api';
import { browserTimeZone, relative, timeZones, zonedTimeToUtc } from './scheduleText';

const utcFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', hour: '2-digit',
  minute: '2-digit', hourCycle: 'h23' });

export default function ScheduleDialog({ open, onOpenChange, campaign, maxLatenessMinutes, onScheduled }) {
  const zones = useMemo(() => timeZones(), []);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [tz, setTz] = useState(browserTimeZone());
  const [tzOpen, setTzOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null); // { detail, problems }

  useEffect(() => {
    if (!open) return;
    setDate(''); setTime(''); setTz(browserTimeZone()); setError(null);
  }, [open]);

  const instant = date && time ? zonedTimeToUtc(`${date}T${time}`, tz) : undefined; // undefined: incomplete, null: gap
  let preview = null;
  let previewBad = false;
  if (instant === null) {
    preview = `That time doesn't exist in ${tz} (clocks go forward)`; previewBad = true;
  } else if (instant) {
    if (instant.getTime() < Date.now() + 60000) { preview = 'Pick a time in the future'; previewBad = true; }
    else preview = `= ${utcFmt.format(instant)} UTC · ${relative(instant)}`;
  }
  const canSubmit = !!instant && !previewBad && !submitting;

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await scheduleCampaign(campaign.id, `${date}T${time}`, tz);
      toast.success('Campaign scheduled');
      onOpenChange(false);
      onScheduled();
    } catch (err) {
      const data = err.response?.data || {};
      const d = data.detail && typeof data.detail === 'object' ? data.detail : data;
      if (err.response?.status === 400 && Array.isArray(d.problems) && d.problems.length) {
        setError({ detail: d.detail || 'Fix the pre-launch problems first', problems: d.problems });
      } else if (err.response?.status === 400 && typeof data.detail === 'string') {
        setError({ detail: data.detail, problems: [] }); // bad time / zone: show next to the form
      } else {
        toast.error((typeof data.detail === 'string' && data.detail) || 'Failed to schedule');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="bg-[#18181B] border-[#27272A]" data-testid="schedule-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Schedule launch</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Pick when this campaign should go live.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 my-4">
            <div>
              <Label htmlFor="schedule-date">Date</Label>
              <Input id="schedule-date" type="date" value={date} onChange={(e) => { setDate(e.target.value); setError(null); }}
                className="mt-1 bg-[#09090B] border-[#27272A]" data-testid="schedule-date" />
            </div>
            <div>
              <Label htmlFor="schedule-time">Time (24h)</Label>
              <Input id="schedule-time" type="time" value={time} onChange={(e) => { setTime(e.target.value); setError(null); }}
                className="mt-1 bg-[#09090B] border-[#27272A]" data-testid="schedule-time" />
            </div>
          </div>
          <Label>Time zone</Label>
          <Popover open={tzOpen} onOpenChange={setTzOpen} modal>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" role="combobox" aria-expanded={tzOpen}
                className="mt-1 w-full justify-between border-[#27272A] bg-[#09090B] font-normal" data-testid="schedule-tz">
                {tz}
                <ChevronsUpDown className="w-4 h-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] bg-[#18181B] border-[#27272A]" align="start">
              <Command>
                <CommandInput placeholder="Search time zones…" data-testid="schedule-tz-search" />
                <CommandList>
                  <CommandEmpty>No time zone found.</CommandEmpty>
                  <CommandGroup>
                    {zones.map((z) => (
                      <CommandItem key={z} value={z} onSelect={() => { setTz(z); setTzOpen(false); setError(null); }}>
                        <Check className={`w-4 h-4 mr-2 ${z === tz ? 'opacity-100' : 'opacity-0'}`} />
                        {z}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {preview && (
            <p className={`text-sm mt-3 ${previewBad ? 'text-amber-400' : 'text-[#A1A1AA]'}`} data-testid="schedule-preview">{preview}</p>
          )}

          {error && (
            <div className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" role="alert" data-testid="schedule-error">
              <p className="font-medium">{error.detail}</p>
              {error.problems.length > 0 && (
                <ul className="mt-1 space-y-1 list-disc pl-5">
                  {error.problems.map((p, i) => (
                    <li key={`${p.code}-${i}`} data-testid="schedule-problem">{p.menuName ? `${p.menuName}: ` : ''}{p.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <p className="text-xs text-[#71717A] mt-3">
            If the server is down at that time, a launch more than {maxLatenessMinutes ?? 'a few'} minutes late is skipped and you'll get an email.
          </p>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" className="border-[#27272A]" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" className="bg-[#06C755] hover:bg-[#05b34c] text-black" disabled={!canSubmit} data-testid="schedule-confirm">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Schedule
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
